// Pure helpers used by the bulk-submit flow.
//
// Kept in their own file (no React, no hook, no transitive imports that
// pull in `localStorage`) so they can be unit-tested in a Node test
// environment without spinning up jsdom or react-query.

import type {
  MFRequestableRole,
  RoleRequestInput,
} from "state/members/access_v2/catalogs";
import { EVERY_RECIPIENT, resourceKey, type FlatRequestItem } from "./types";
import type { BasketItemCfg } from "./BasketTable";
import type { BasketRecipient, ResolvedRecipient } from "./BasketDrawer";
import type { DurationValue } from "components/ds/DurationField";
import {
  fitToRole,
  objectOf,
  type RequestFor,
} from "state/members/access_v2/roleRequests";
import { checkDurationAgainstMax } from "./durationLimits";
import {
  itemRoleProblem,
  itemTerms,
  recipientRoles,
  requestableRoles,
  roleDurationProblem,
  selectedRoles,
  type RecipientRoleSelection,
  type RoleSelection,
} from "./roleSelection";
import { catalogView, type ItemView } from "./recipientViews";

// Convert plural picker units ("days") to the singular form the backend
// expects ("day").
export const SINGULARIZE: Record<string, string> = {
  hours: "hour",
  days: "day",
  weeks: "week",
  months: "month",
  years: "year",
  minutes: "minute",
};

// Canonical key for a per-(recipient × item) override in `perItemCfg`.
// Defined ONCE here because submit, the drawer display, and the warning
// preview all read/write this map — a drifted format would silently orphan
// per-item durations with no type error.
export const perItemKey = (recipientId: string, itemId: string) =>
  `${recipientId}__${itemId}`;

// Per-(recipient × item × account) duration override key. When a resource fans
// out to several accounts in per-item mode, each account is its own request and
// carries its own duration; absent an override it inherits the resource's.
export const perAccountKey = (
  recipientId: string,
  itemId: string,
  identityId: string,
) => `${recipientId}__${itemId}__${identityId}`;

// Per-(recipient × resource × role) duration override key, on a resource whose
// access follows its roles. Keyed on the resource like the role selection, so a
// resource re-added from another catalog keeps its roles' durations with their
// ticks. An override is the role's duration on every account the resource fans
// out to. Keyed on EVERY_RECIPIENT it is the role's duration for whoever the
// request is for (the mobile sheet has no per-recipient rows).

export const perRoleKey = (
  recipientId: string,
  resourceId: string,
  roleId: string,
) => `${recipientId}__${resourceId}__${roleId}`;

/** Inputs shared by everything that needs a row's effective config. */
export interface ItemCfgContext {
  perItem: boolean;
  perItemCfg: Record<string, BasketItemCfg>;
  bulkDuration: DurationValue;
  bulkReason: string;
  perAccountDuration: Record<string, DurationValue>;
  /** `${recipientId}__${itemId}__${roleId}` → per-role duration override. */
  perRoleDuration: Record<string, DurationValue>;
}

// The effective config for a (recipient, item): the per-item override when
// customise-per-item is on, else the bulk fallback. Single source of truth
// shared by submit (buildBulkPayloads), the drawer display (BasketDrawer),
// and the catalog/basket warning previews — they MUST agree or the duration
// shown differs from the duration sent.
export const resolveItemCfg = (
  recipientId: string,
  itemId: string,
  { perItem, perItemCfg, bulkDuration, bulkReason }: ItemCfgContext,
): BasketItemCfg => {
  const fallback = { duration: bulkDuration, reason: bulkReason };
  if (!perItem) return fallback;
  return perItemCfg[perItemKey(recipientId, itemId)] ?? fallback;
};

// The effective duration for a specific account of a (recipient, item): a
// per-account override when customising, else the resource's duration. Shared
// by the sub-row duration fields and the submit fan-out so they can't drift.
export const resolveAccountDuration = (
  recipientId: string,
  itemId: string,
  identityId: string,
  ctx: ItemCfgContext,
): DurationValue =>
  (ctx.perItem
    ? ctx.perAccountDuration[perAccountKey(recipientId, itemId, identityId)]
    : undefined) ?? resolveItemCfg(recipientId, itemId, ctx).duration;

// The duration a ticked role is requested for on an account: the recipient's
// override when customise-per-item is on, else the role's override for every
// recipient, else the account's, the item's or the bulk duration; fitted to the
// role's terms. The role fields, the "up to" notes and the submit fan-out all
// read it.
export const resolveRoleDuration = (
  recipientId: string,
  item: FlatRequestItem,
  role: MFRequestableRole,
  identityId: string | undefined,
  ctx: ItemCfgContext,
): DurationValue =>
  fitToRole(
    (ctx.perItem
      ? ctx.perRoleDuration[perRoleKey(recipientId, resourceKey(item), role.id)]
      : undefined) ??
      ctx.perRoleDuration[
        perRoleKey(EVERY_RECIPIENT, resourceKey(item), role.id)
      ] ??
      (identityId
        ? resolveAccountDuration(recipientId, item.id, identityId, ctx)
        : resolveItemCfg(recipientId, item.id, ctx).duration),
    role.terms,
  );

export const notRequestableFor = (requestFor: RequestFor) =>
  `This resource can't be requested for ${objectOf(requestFor)}.`;

// Why a member's request on this row won't be sent, judged on their own view
// of it, or null when it will. Submit skips exactly these rows and the basket
// flags them, so the two can't disagree.
export const memberRowProblem = (
  recipientId: string,
  item: FlatRequestItem | null,
  selection: RoleSelection,
  ctx: ItemCfgContext,
  requestFor: RequestFor = "you",
): string | null => {
  if (!item) return notRequestableFor(requestFor);
  const roleProblem = itemRoleProblem(item, selection, requestFor);
  if (roleProblem) return roleProblem;
  const roleDuration = selectedRoles(item, selection)
    .map((role) =>
      roleDurationProblem(
        role,
        resolveRoleDuration(recipientId, item, role, undefined, ctx),
      ),
    )
    .find(Boolean);
  if (roleDuration) return roleDuration;
  const check = checkDurationAgainstMax(
    resolveItemCfg(recipientId, item.id, ctx).duration,
    itemTerms(item, selection),
  );
  return check.exceeds ? (check.reason ?? null) : null;
};

export type RecipientRowStatus = {
  /** Sent to nobody this recipient covers. */
  warning?: string;
  /** A group's row that some of its members won't be sent. */
  note?: string;
  /** Someone it is sent for must give a reason. */
  requiresComment: boolean;
};

export const recipientRequestFor = (
  recipient: BasketRecipient,
  selfMemberId: string | undefined,
): RequestFor =>
  recipient.id === selfMemberId ? "you" : { name: recipient.name };

// A group's members are told apart only by count, so what each misses is said
// of "them"; a row nobody in it gets names the group.
export const recipientRowStatus = (
  recipient: ResolvedRecipient,
  item: FlatRequestItem,
  view: ItemView,
  selection: RecipientRoleSelection,
  ctx: ItemCfgContext,
  selfMemberId?: string,
): RecipientRowStatus => {
  const roles = recipientRoles(selection, recipient.id);
  const named = recipientRequestFor(recipient, selfMemberId);
  const memberFor = recipient.isGroup ? "them" : named;
  const problems: string[] = [];
  let requiresComment = false;
  for (const memberId of recipient.memberIds) {
    const memberItem = view(memberId, item);
    const problem = memberRowProblem(
      recipient.id,
      memberItem,
      roles,
      ctx,
      memberFor,
    );
    if (problem) problems.push(problem);
    else if (memberItem && itemTerms(memberItem, roles).requiresComment)
      requiresComment = true;
  }
  if (problems.length === 0) return { requiresComment };
  if (problems.length === recipient.memberIds.length)
    return {
      warning: recipient.isGroup
        ? memberRowProblem(
            recipient.id,
            view(recipient.memberIds[0], item),
            roles,
            ctx,
            named,
          )!
        : problems[0],
      requiresComment,
    };
  return {
    note: `Won't be sent for ${problems.length} of ${recipient.memberIds.length} people: ${problems[0]}`,
    requiresComment,
  };
};

const roleRequestInput = (
  roleId: string,
  duration: DurationValue,
): RoleRequestInput =>
  duration.indefinite
    ? { entitlementId: roleId, indefiniteRequest: true }
    : {
        entitlementId: roleId,
        indefiniteRequest: false,
        requestedForUnit: SINGULARIZE[duration.unit] ?? duration.unit,
        requestedForValue: duration.num,
      };

export interface SubmitRequestArgs {
  /** Already-expanded recipients — groups have been flattened to memberIds. */
  recipients: ResolvedRecipient[];
  items: FlatRequestItem[];
  bulkDuration: DurationValue;
  bulkReason: string;
  perItem: boolean;
  perItemCfg: Record<string, BasketItemCfg>;
  /** `${recipientId}__${itemId}__${identityId}` → per-account duration override. */
  perAccountDuration: Record<string, DurationValue>;
  /** `${recipientId}__${resourceId}__${roleId}` → per-role duration override. */
  perRoleDuration: Record<string, DurationValue>;
  /**
   * Bulk mode only: when true, the bulk reason is attached only to resources
   * whose `requiresComment` is set; other resources submit with no reason. When
   * false, every resource gets the bulk reason. Ignored in per-item mode (each
   * row carries its own reason).
   */
  reasonOnlyRequired: boolean;
  roleSelection: RecipientRoleSelection;
  /** Each member's own view of a row; the catalog's when omitted. */
  itemView?: ItemView;
  /**
   * itemId → chosen identityIds (PLO-4704). A resource can be requested for
   * several of the requester's accounts at once, so this is a list and each id
   * fans out into its own request. Applied only to the self recipient's rows
   * (`selfMemberId`); other recipients submit with the backend default.
   */
  identityByItem?: Record<string, string[]>;
  /** The current user's member id — identity choices apply only to their rows. */
  selfMemberId?: string;
  /**
   * Self-recipient items whose account picker is shown but has nothing chosen.
   * These are dropped for the SELF recipient only (the user cleared them) — other
   * recipients still get the resource with the backend default. Scoping this per
   * recipient (not per item) keeps a group's valid row from being skipped just
   * because the requester's own account choice was empty.
   */
  identityIncompleteItems?: string[];
}

// The submit-time payload shape — matches the camelCase shape the backend
// hook (`useBulkCreateAccessRequests`) accepts. Kept structural to avoid
// importing the state-layer type and dragging hook deps into tests. A role
// request carries a duration per role and none of its own.
export interface BulkPayload {
  catalogItemId: string;
  selectableResourceId: string;
  accessRequestedForMemberId: string;
  indefiniteRequest?: boolean;
  requestedForUnit?: string;
  requestedForValue?: number;
  comments?: string;
  /** Which of the requester's accounts the grant lands on (PLO-4704). */
  identityId?: string;
  /** The roles asked for, on a resource whose access follows them. */
  roles?: RoleRequestInput[];
}

// Expand (recipients × memberIds × items) into the per-row payloads the
// backend's bulk endpoint accepts.
export const buildBulkPayloads = (args: SubmitRequestArgs): BulkPayload[] => {
  const payloads: BulkPayload[] = [];
  // Unit/value are placeholders when indefinite (backend ignores them) but
  // always sent so the shape is stable.
  const durationFields = (d: DurationValue) => ({
    indefiniteRequest: Boolean(d.indefinite),
    requestedForUnit: SINGULARIZE[d.unit] ?? d.unit,
    requestedForValue: d.num,
  });
  const view = args.itemView ?? catalogView;
  for (const r of args.recipients) {
    const selection = recipientRoles(args.roleSelection, r.id);
    for (const memberId of r.memberIds) {
      for (const basketItem of args.items) {
        const item = view(memberId, basketItem);
        if (!item || memberRowProblem(r.id, item, selection, args)) continue;
        const cfg = resolveItemCfg(r.id, item.id, args);
        const terms = itemTerms(item, selection);
        const roles = requestableRoles(item)
          ? selectedRoles(item, selection)
          : null;
        // Per-item mode: each row owns its reason. Bulk mode: the bulk reason
        // reaches a row only when scope is "all" or the row requires it.
        const attachReason =
          args.perItem ||
          !args.reasonOnlyRequired ||
          terms.requiresComment;
        const base = {
          catalogItemId: item.catalogItemId,
          selectableResourceId: item.resource.selectableResource.id,
          accessRequestedForMemberId: memberId,
          // Trim so the sent value matches the trimmed check the basket gate
          // uses (isReasonInvalid) — a whitespace-only reason is "blank".
          comments: attachReason ? cfg.reason.trim() || undefined : undefined,
        };
        // A role row's durations are its roles', each fitted to the role, so
        // it is never over a cap; a resource row takes the row's own duration.
        const durationPart = (identityId?: string) =>
          roles
            ? {
                roles: roles.map((role) =>
                  roleRequestInput(
                    role.id,
                    resolveRoleDuration(r.id, item, role, identityId, args),
                  ),
                ),
              }
            : durationFields(
                identityId
                  ? resolveAccountDuration(r.id, item.id, identityId, args)
                  : cfg.duration,
              );
        // Only the requester's own rows carry an account choice; everyone else
        // takes the backend default (we don't hold their identities). A row can
        // target several accounts → one request per chosen account, each with
        // its own (possibly customised) duration.
        const isSelf =
          Boolean(args.selfMemberId) && memberId === args.selfMemberId;
        const identityIds = isSelf ? args.identityByItem?.[item.id] ?? [] : [];
        if (identityIds.length === 0) {
          // The self recipient cleared a multi-account resource → skip it for
          // them, but other recipients still get the backend default.
          if (isSelf && (args.identityIncompleteItems ?? []).includes(item.id)) {
            continue;
          }
          payloads.push({ ...base, ...durationPart(), identityId: undefined });
        } else {
          for (const identityId of identityIds) {
            // Drop just the over-max account — its sibling accounts and other
            // recipients still submit.
            if (
              !roles &&
              checkDurationAgainstMax(
                resolveAccountDuration(r.id, item.id, identityId, args),
                terms,
              ).exceeds
            ) {
              continue;
            }
            payloads.push({ ...base, ...durationPart(identityId), identityId });
          }
        }
      }
    }
  }
  return payloads;
};

// Dedupe error messages from the bulk response — typical case is 5 rows
// failing with the same "already requested" string and we don't want to
// repeat it 5 times in the toast.
export const dedupeErrors = (
  results: Array<{ ok: boolean; error?: string }>,
): string[] =>
  results
    .filter((r) => !r.ok)
    .map((r) => r.error || "Unknown error")
    .filter((msg, i, a) => a.indexOf(msg) === i);
